import React from 'react'
import { Link } from 'react-router-dom'

import TextEditor from '../components/TextEditor/TextEditor'
import Pattern from '../components/Pattern/Pattern'
import Button from '../components/Button/Button'
import DocThumbnail from '../components/Doc/DocThumbnail'

import { revertToPristine } from '../actions/actions.export';

let fixedScale = 3;

const doc = {
  title: '.PAT from Scratch: Intro Tutorial',
  slug: 'pat-from-scratch-intro-tutorial',
  tags: ['education'],
  category: 'pat-file-basics',
  content: (props, docLoader) => {
      return <div>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-12">
          <div className="doc-callout doc-callout-primary">
            <span className="doc-callout-type-label"><i className="fas fa-exclamation-circle"/> HEADS UP</span>
            <strong>Before reading...</strong>
            <p className="p-push-down">
              This tutorial is aimed at anyone who wants to better understand how patterns are defined within a .PAT file. Before diving in, we recommend reading <Link to="/docs/d/what-is-a-pat-file">What is a .PAT File?</Link> and <Link to="/docs/d/pat-file-syntax-and-rules">.PAT File Syntax & Rules</Link>
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <p>
            For this tutorial, we're only going to need to make changes to the rows in a .PAT file that make up the pattern definition.
          </p>
          <p>
            This section is structured like a very simple table, using commas to separate each column. It can help to use spaces to keep the values in each column lined up, but they aren't required.
          </p>
          <p>
            We'll walk through the 7 parameters that are used to draw each line:<br/><code>ANGLE</code>, <code>X</code>, <code>Y</code>, <code>SHIFT</code>, <code>OFFSET</code>, <code>DASH</code>, and <code>SPACE</code>
          </p>
        </div>
        <div className="col-md-6">
          <pre>
<span className="translucent">;%UNITS=INCH<br/>
;%VERSION=3.0<br/>
;<br/>
*Chevron<br/>
;%TYPE=MODEL<br/>
;</span><br/>
<strong>;angle, x,      y,      shift,  offset, dash,   space<br/>
45,     0,      0,      4,      4,      16,     -16<br/>
135,    0,      0,      -4,     4,      16,     -16<br/>
90,     0,      0,      0,      11.3137<br/></strong></pre>
        </div>
      </div>
      {/*<div className="row doc-row row-push-down-extra">
        <div className="col-md-12">
          <h4>Today we'll be making a simple chevron pattern</h4>
        </div>
        <div className="col-md-6">
          <p className="">This simple file...</p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor cacheID={'tutorial_intro_01'} form="tutorial_intro_01"/>
          </div>
        </div>

        <div className="col-md-6">
          <p className="">...turns into this pattern.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} gui={false} pattern={props.patternManager.cache['tutorial_intro_01']}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_01')) }}
              />
          </div>
        </div>
      </div>*/}
      <div className="row doc-row">
        <div className="col-md-12">
          <h4><strong>Step 1:</strong> Learning about <code>ANGLE</code></h4>
          <p>
            The first number in each row controls the line's angle. Angles are defined in degrees, rotating counter-clockwise around the origin of the line. 
          </p>
        </div>
      </div>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-6">
          <p className="">Change the number <code>0</code> under <code>ANGLE</code> to <code>45</code>...</p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor demo={true} cacheID={'tutorial_intro_02'} form="tutorial_intro_02"/>
            <div className="banner banner-yellow">Edit me!</div>
          </div>
        </div>

        <div className="col-md-6">
          <p className="">...and watch how the line rotates.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_02']} gui={true}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_02')) }}
              />
          </div>
        </div>
      </div>

      <h4><strong>Step 2:</strong> Defining a line's origin with <code>X</code> & <code>Y</code></h4>
      <p>
        The 2nd and 3rd number in each row determine its origin, relative to the origin of the pattern's coordinate system. Increasing the <code>X</code> value will move the line to the right and increasing the <code>Y</code> value will move the line upwards.
      </p>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-6">
          <p className="docs-min-lines">The <code>X</code> and <code>Y</code> values for this pattern can be left at <code>0</code>, but feel free to edit both...</p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor demo={true} cacheID={'tutorial_intro_03'} form="tutorial_intro_03"/>
            <div className="banner banner-yellow">Edit me!</div>
          </div>
        </div>

        <div className="col-md-6">
          <p className="docs-min-lines">...and watch how the line moves.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_03']} gui={true}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_03')) }}
              />
          </div>
        </div>
      </div>

      <h4><strong>Step 3:</strong> Cloning the line with <code>OFFSET</code></h4>
      <p>
        We're skipping over <code>SHIFT</code> for a moment, but don't worry we'll come back to it at the end. 
      </p>
      <p>
        Until now, we've had a single line that continues infinitely. The true power of .PAT files comes with the <code>OFFSET</code> field, which controls how the line is cloned and repeated perpendicularly to itself.
      </p>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-6">
          <p className="docs-min-lines">Slowly increase <code>OFFSET</code> below from <code>0</code> to <code>4</code>...</p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor demo={true} cacheID={'tutorial_intro_04'} form="tutorial_intro_04"/>
            <div className="banner banner-yellow">Edit me!</div>
          </div>
        </div>

        <div className="col-md-6">
          <p className="docs-min-lines">...and watch how the line clones itself and then spreads apart.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_04']} gui={true}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_04')) }}
              />
          </div>
        </div>
      </div>

      <h4><strong>Step 4:</strong> Chopping lines up with <code>DASH</code> & <code>SPACE</code></h4>
      <p>
        While some patterns do have solid lines, dashed lines are more common. Creating a dash pattern is done using the <code>DASH</code> and <code>SPACE</code> parameters. <code>DASH</code> defines the length of each segment and <code>SPACE</code> defines the gap between segments. <code>DASH</code> values are always positive and <code>SPACE</code> values are always negative.
      </p>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-6">
          <p className="docs-min-lines">Set <code>DASH</code> to <code>16</code> and <code>SPACE</code> to <code>-16</code>...</p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor demo={true} cacheID={'tutorial_intro_05'} form="tutorial_intro_05"/>
            <div className="banner banner-yellow">Edit me!</div>
          </div>
        </div>

        <div className="col-md-6">
          <p className="docs-min-lines">...and watch breaks appear in every line.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_05']} gui={true}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_05')) }}
              />
          </div>
        </div>
      </div>


      <h4><strong>Step 5:</strong> Adjusting orientation with <code>SHIFT</code></h4>
      <p>
        <code>SHIFT</code> can be very confusing to those new to .PAT files. With a <code>SHIFT</code> value of <code>0</code> the lines we created with <code>OFFSET</code> are all aligned with each other. As each line is repeated, the <code>SHIFT</code> values move the origin of each individual line long its axis. This has no impact on solid lines, but lines with <code>DASH</code> and <code>SPACE</code> values can be <i>shifted</i> dramatically.
      </p>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-6">
          <p className="docs-min-lines">Slowly increase the value under <code>SHIFT</code> from <code>0</code> to <code>4</code>...</p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor demo={true} cacheID={'tutorial_intro_06'} form="tutorial_intro_06"/>
            <div className="banner banner-yellow">Edit me!</div>
          </div>
        </div>

        <div className="col-md-6">
          <p className="docs-min-lines">...and watch each line shift along its axis.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_06']} gui={true}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_06')) }}
              />
          </div>
        </div>
      </div>


      <h4><strong>Step 6:</strong> Adding a second line</h4>
      <p>
        Since each line is configured by its own row, it's easy to add more by copying and pasting or simply typing in the next line.
      </p>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-6">
          <p className="docs-min-lines">Copy the row we just completed and paste it on a new line below it. Then, adjust the <code>ANGLE</code> of the new line to <code>135</code> and its <code>SHIFT</code> value to <code>-4</code></p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor demo={true} cacheID={'tutorial_intro_07'} form="tutorial_intro_07"/>
            <div className="banner banner-yellow">Edit me!</div>
          </div>
        </div>

        <div className="col-md-6">
          <p className="docs-min-lines">...and watch the pattern begin to take shape.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_07']} gui={true}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_07')) }}
              />
          </div>
        </div>
      </div>


      <h4><strong>Step 7:</strong> Finishing touches </h4>
      <p>
        Our chevron pattern needs one last piece: a vertical line with each zig-zag. It will require a little bit of math to get the spacing just right, but we'll handle that for you.
      </p>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-6">
          <p className="docs-min-lines">Add a new line with a <code>90</code> degree <code>ANGLE</code>, an <code>OFFSET</code> of <code>11.3137</code> and <code>0</code> for all other values.</p>
          <div className="col-themed col-themed-no-pad theme-dark" style={{height: 250}}>
            <TextEditor demo={true} cacheID={'tutorial_intro_08'} form="tutorial_intro_08"/>
            <div className="banner banner-yellow">Edit me!</div>
          </div>
        </div>

        <div className="col-md-6">
          <p className="docs-min-lines">...and watch the final line take its place.</p>
          <div className="col-themed col-themed-no-pad" style={{height: 250}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_08']} gui={true}/>
          </div>
        </div>
        <div className="col-md-12">
          <div className="list-right">
            <Button
              display="Reset"
              onClick={(e)=>{ props.dispatch(revertToPristine('tutorial_intro_08')) }}
              />
          </div>
        </div>
      </div>

      <h4><strong>Step 8:</strong> Celebrate 🎉</h4>
      <p>
        You made a pattern from scratch! If everything went well, you should have something that looks like this:
      </p>
      <div className="row doc-row row-push-down-extra">
        <div className="col-md-12">
          <div className="col-themed col-themed-no-pad" style={{height: 350}}>
            <Pattern colors={false} fixedScale={fixedScale} pattern={props.patternManager.cache['tutorial_intro_09']} gui={false}/>
          </div>
        </div>
      </div>

      <h4><strong>Step 9:</strong> But wait, there's more!</h4>
      <p>
        Hopefully you better understand .PAT files after going through this tutorital. But, if you still feel unsure about how to create another pattern from scratch or even just a variation of this chevron with different measurements - don't worry, we did too.
      </p>
      <p className="lead">
        That's why Pattycake offers three ways to create patterns:
      </p>
      <div className="row doc-row row-padded">
        <div className="col-md-4">
          <div className="">
            <h5>1. Pattern Templates</h5>
            <p>
              Tweak parameters to instantly generate commonly used patterns like brick and title.
            </p>
            <Link to="/new/template" className="col-themed-link">
              <Button
                display={<span>New Pattern from a Template &nbsp;<i className="fad fa-angle-double-right"/></span>}
                block={true}
                color="primary"
                />
            </Link>
          </div>
        </div>

        <div className="col-md-4">
          <div className="">
            <h5>2. Drawing Canvas</h5>
            <p >
              Draw your pattern and let Pattycake handle the complex math to make each line .PAT compatible 
            </p>
            <Link to="/new/drawing" className="col-themed-link">
              <Button
                display={<span>New Pattern from a Drawing &nbsp;<i className="fad fa-angle-double-right"/></span>}
                block={true}
                
                color="primary"
              />
            </Link>
          </div>
        </div>

        <div className="col-md-4">
          <div className="">
            <h5>3. .PAT Editor</h5>
            <p >
              A feature-packed text editor that offers line toggling, error checking, duplication, and more
            </p>
             
            <Link to="/new" className="col-themed-link">
              <Button
                display={<span>New Pattern &nbsp;<i className="fad fa-angle-double-right"/></span>}
                block={true}
                
                color="primary"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  }
}

export default doc;


// WEBPACK FOOTER //
// ./src/docs/pat-from-scratch-intro-tutorial.js